import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';

import { firestore } from '../firebase';
import { displayNameOf, type User } from '../../shared/schemas';
import { userFromDoc, usersFromSnapshot } from '../lib/users';
import { useAppState } from '../lib/appState';

/**
 * Must stay at or under the cap in firestore.rules, which is the only thing
 * that language can check about a list query.
 */
const SEARCH_LIMIT = 10;

/**
 * Following is one-way. The UI has always said "friend", but `users.friends` is
 * a list of people you follow, and they do not have to follow you back.
 */
function Friends() {
  const { userId } = useAppState();

  const [friends, setFriends] = useState<User[]>([]);
  const [results, setResults] = useState<User[]>([]);
  const [term, setTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * Reads the follow list, then each profile individually.
   *
   * One `getDoc` per follow rather than a `documentId() in [...]` query, because
   * `allow get` covers reading a profile by id unconditionally while `allow
   * list` is capped and was, until this branch, denying every list outright. It
   * also sidesteps the 30-value cap on `in` (gotcha 6) without chunking.
   *
   * The cost is one read per follow instead of one per thirty. At the size this
   * game runs at that is not worth a query for.
   */
  const loadFriends = useCallback(async () => {
    try {
      const ids = userFromDoc(await getDoc(doc(firestore, 'users', userId)), 'Friends')?.friends ?? [];

      if (ids.length === 0) {
        setFriends([]);
        return;
      }

      const profiles = await Promise.all(
        ids.map((id) => getDoc(doc(firestore, 'users', id))),
      );

      setFriends(
        profiles
          .map((snapshot) => userFromDoc(snapshot, 'Friends'))
          .filter((user): user is User => user !== null),
      );
      setError(null);
    } catch (err) {
      console.error('[Friends] could not load follow list:', err);
      setError('Could not load your friends. Try refreshing.');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadFriends();
  }, [loadFriends]);

  const handleSearch = async (event: React.FormEvent) => {
    event.preventDefault();
    const needle = term.trim().toLowerCase();
    if (!needle) return;

    setSearching(true);
    setError(null);

    try {
      // Prefix search over the lowercased copy of the display name. U+F8FF is
      // above any normal character, so it bounds the range at the prefix.
      const snapshot = await getDocs(
        query(
          collection(firestore, 'users'),
          where('settings.displayNameLower', '>=', needle),
          where('settings.displayNameLower', '<=', `${needle}`),
          limit(SEARCH_LIMIT),
        ),
      );

      const found = usersFromSnapshot(snapshot, 'Friends').filter(
        (user) => user.id !== userId && !friends.some((friend) => friend.id === user.id),
      );

      setResults(found);
      if (found.length === 0) {
        setError(`Punfortunately, nobody here goes by "${term.trim()}".`);
      }
    } catch (err) {
      console.error('[Friends] search failed:', err);
      setError('Search failed. Try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleAdd = async (friendId: string) => {
    setBusyId(friendId);
    setError(null);

    try {
      // arrayUnion rather than read-modify-write, so two adds cannot clobber.
      await updateDoc(doc(firestore, 'users', userId), { friends: arrayUnion(friendId) });
      setResults((current) => current.filter((user) => user.id !== friendId));
      setTerm('');
      await loadFriends();
    } catch (err) {
      console.error('[Friends] could not follow:', err);
      setError('Could not add that person. Try again.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <h1 className="mb-6 font-display text-2xl font-bold">Friends</h1>

      <form onSubmit={handleSearch} className="flex gap-2">
        <input
          type="text"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="Search by username"
          className="min-w-0 flex-1 rounded-xl border-2 border-ink bg-white px-4 py-3 shadow-pop-sm outline-none placeholder:text-ink/35 focus:shadow-pop"
        />
        <button
          type="submit"
          disabled={searching || !term.trim()}
          className="press shrink-0 rounded-xl border-2 border-ink bg-grape px-5 py-3 font-display font-bold text-white shadow-pop-sm disabled:cursor-not-allowed disabled:bg-ink/10 disabled:text-ink/30"
        >
          {searching ? '...' : 'Search'}
        </button>
      </form>

      {error && (
        <p className="mt-4 rounded-xl border-2 border-ink bg-coral px-4 py-3 text-sm font-medium text-white shadow-pop-sm">
          {error}
        </p>
      )}

      {results.length > 0 && (
        <div className="mt-5 space-y-3">
          {results.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between gap-3 rounded-xl border-2 border-ink bg-white px-4 py-3 shadow-pop-sm"
            >
              <span className="flex min-w-0 items-center gap-3">
                <Avatar name={displayNameOf(user)} />
                <span className="truncate font-display font-bold">{displayNameOf(user)}</span>
              </span>
              <button
                onClick={() => handleAdd(user.id)}
                disabled={busyId === user.id}
                className="press shrink-0 rounded-full border-2 border-ink bg-mustard px-4 py-1.5 font-display text-sm font-bold shadow-pop-sm disabled:opacity-50"
              >
                {busyId === user.id ? 'Adding' : 'Follow'}
              </button>
            </div>
          ))}
        </div>
      )}

      <h2 className="mb-4 mt-10 font-display text-lg font-bold">
        Following {friends.length > 0 && <span className="text-ink/45">({friends.length})</span>}
      </h2>

      {loading ? (
        <div className="flex justify-center py-10">
          <span className="h-8 w-8 animate-spin rounded-full border-[3px] border-ink/15 border-t-ink" />
        </div>
      ) : friends.length === 0 ? (
        <p className="rounded-2xl border-2 border-dashed border-ink/30 bg-white/60 px-6 py-10 text-center text-sm text-ink/60">
          Nobody yet. Puns land better with an audience.
        </p>
      ) : (
        <div className="space-y-3">
          {friends.map((friend) => (
            <Link
              key={friend.id}
              to={`/user/${friend.id}`}
              className="press flex items-center gap-3 rounded-xl border-2 border-ink bg-white px-4 py-3 shadow-pop-sm hover:bg-mustard/20"
            >
              <Avatar name={displayNameOf(friend)} />
              <span className="truncate font-display font-bold">{displayNameOf(friend)}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Avatar({ name }: { name: string }) {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-ink bg-lagoon font-display text-sm font-bold text-white">
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

export default Friends;
